<template>
  <div class="car-view-details-modal">
    <div class="modal-card">
      <!-- Header (Fixed) -->
      <header class="modal-card-head">
        <div class="vendor-header">
          <img v-if="vendorLogo" :src="vendorLogo" class="vendor-logo" :alt="vendorName" />
          <span v-else class="vendor-name-text">{{ vendorName }}</span>
          <span class="header-title">Rate details</span>
        </div>
        <button type="button" class="delete" @click="$emit('close')" />
      </header>

      <!-- Scrollable Body -->
      <div class="modal-body-scroll">
        <!-- Vehicle Info Section -->
        <section class="vehicle-section">
        <img 
          :src="carPhoto" 
          class="vehicle-image" 
          :alt="vehicleName"
          @error="handleImageError"
        />
        <div class="vehicle-info">
          <p class="vehicle-name">{{ vehicleName }}</p>
          <p class="vehicle-description">{{ vehicleDescription }}</p>
          <div class="vehicle-specs">
            <span class="spec-tag">{{ driveType }}</span>
            <span class="spec-tag">{{ passengers }} seats</span>
            <span v-if="doors" class="spec-tag">{{ doors }} doors</span>
          </div>
        </div>
      </section>

      <!-- Itinerary Section -->
      <section class="itinerary-section">
        <div class="itinerary-column">
          <p class="itinerary-label">Pick-up</p>
          <p class="itinerary-datetime">{{ formattedPickupDate }}</p>
          <p class="itinerary-location">{{ pickupLocationName }}</p>
          <div class="map-placeholder">
            <GMap v-if="pickupLocation && pickupLocation.position" 
                  :lat="pickupLocation.position.lat" 
                  :long="pickupLocation.position.lng" 
                  carOrHotel="car" 
                  width="100%" 
                  height="90" />
          </div>
          <p class="itinerary-address">{{ pickupAddress }}</p>
          <div v-if="pickupHours" class="itinerary-hours">
            <span class="hours-label">Hours:</span>
            <span class="hours-value">{{ pickupHours }}</span>
          </div>
        </div>

        <div class="itinerary-column">
          <p class="itinerary-label">Drop-off</p>
          <p class="itinerary-datetime">{{ formattedDropoffDate }}</p>
          <p class="itinerary-location">{{ dropoffLocationName }}</p>
          <div class="map-placeholder">
            <GMap v-if="dropoffLocation && dropoffLocation.position" 
                  :lat="dropoffLocation.position.lat" 
                  :long="dropoffLocation.position.lng" 
                  carOrHotel="car" 
                  width="100%" 
                  height="90" />
          </div>
          <p class="itinerary-address">{{ dropoffAddress }}</p>
          <div v-if="dropoffHours" class="itinerary-hours">
            <span class="hours-label">Hours:</span>
            <span class="hours-value">{{ dropoffHours }}</span>
          </div>
        </div>
      </section>

      <!-- Pricing Section -->
      <section class="pricing-section">
        <p class="pricing-label">Rate summary</p>
        <div class="pricing-row">
          <span class="pricing-description">{{ formattedDailyRate }} × {{ rentalDays }} day{{ rentalDays !== 1 ? 's' : '' }}</span>
          <span class="pricing-amount">{{ formattedSubtotal }}</span>
        </div>
        <div v-if="taxesTotal" class="pricing-row">
          <span class="pricing-description">Taxes & fees (est.)</span>
          <span class="pricing-amount">{{ formattedTaxes }}</span>
        </div>
        <div class="pricing-divider"></div>
        <div class="pricing-row pricing-total">
          <span>Estimated total</span>
          <span>{{ formattedTotal }}</span>
        </div>
        <button class="pricing-breakdown-link" @click="viewFullPricingBreakdown">
          View full pricing breakdown ↗
        </button>
      </section>
      </div>

      <!-- Footer Actions (Fixed) -->
      <footer class="modal-card-foot">
        <button class="btn-cancel" @click="$emit('close')">Cancel</button>
        <button 
          v-if="isBookable" 
          class="btn-book" 
          @click="bookCar"
        >Book this car</button>
        <span v-else class="not-bookable-text">Call to book</span>
      </footer>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import GMap from "../../GMap/GMap.vue";
import CarPricingBreakdownModal from "./CarPricingBreakdownModal.vue";

export default {
  name: "CarViewDetailsModal",
  components: {
    GMap,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    searchBody: {
      type: Object,
      required: true,
    },
    pickupLocation: {
      type: Object,
      default: null,
    },
    dropoffLocation: {
      type: Object,
      default: null,
    },
  },
  computed: {
    vendorLogo() {
      return this.pickupLocation?.logo || null;
    },
    vendorName() {
      return this.$helpers?.capitalize(this.item.companyInfo?.name) || this.item.companyInfo?.name || "Rental Company";
    },
    carPhoto() {
      const photo = this.item.vehicleInfo?.photo;
      if (!photo || photo === "https://none") {
        return "/images/default-car.png";
      }
      return photo;
    },
    vehicleName() {
      return this.item.vehicleInfo?.displayName || "Vehicle";
    },
    vehicleDescription() {
      return this.item.vehicleInfo?.description || "";
    },
    driveType() {
      return this.item.vehicleInfo?.driveType || "Automatic";
    },
    passengers() {
      return this.item.vehicleInfo?.passengers || 5;
    },
    vehicleCategory() {
      return this.item.vehicleInfo?.bodyTypeGroup || "";
    },
    doors() {
      return this.item.vehicleInfo?.doors || null;
    },
    formattedPickupDate() {
      const dt = this.item.pickupInfo?.dateTime?.date + " " + this.item.pickupInfo?.dateTime?.time;
      return dt ? moment(dt).format("ddd, MMM D · h:mm A") : "";
    },
    formattedDropoffDate() {
      const dt = this.item.dropoffInfo?.dateTime?.date + " " + this.item.dropoffInfo?.dateTime?.time;
      return dt ? moment(dt).format("ddd, MMM D · h:mm A") : "";
    },
    pickupLocationName() {
      if (this.pickupLocation?.airportInfo?.code) {
        return `${this.pickupLocation.airportInfo.code} Airport`;
      }
      return this.pickupLocation?.name || "";
    },
    dropoffLocationName() {
      if (this.dropoffLocation?.airportInfo?.code) {
        return `${this.dropoffLocation.airportInfo.code} Airport`;
      }
      return this.dropoffLocation?.name || "";
    },
    pickupAddress() {
      return this.pickupLocation?.address || "";
    },
    dropoffAddress() {
      return this.dropoffLocation?.address || "";
    },
    pickupHours() {
      return this.formatHours(this.pickupLocation?.hours);
    },
    dropoffHours() {
      return this.formatHours(this.dropoffLocation?.hours);
    },
    rentalDays() {
      const pickup = moment(this.item.pickupInfo?.dateTime?.date);
      const dropoff = moment(this.item.dropoffInfo?.dateTime?.date);
      return Math.max(1, dropoff.diff(pickup, "days"));
    },
    dailyRate() {
      return this.item.pricingInfo?.averagePricePerDay || 0;
    },
    totalPrice() {
      return this.item.pricingInfo?.total || 0;
    },
    currencyCode() {
      return this.item.pricingInfo?.currencyCode || "USD";
    },
    formattedDailyRate() {
      return this.$options.filters.formatPriceFromAmount(this.dailyRate.toFixed(0), 0, this.currencyCode);
    },
    formattedSubtotal() {
      const subtotal = this.dailyRate * this.rentalDays;
      return this.$options.filters.formatPriceFromAmount(subtotal.toFixed(0), 0, this.currencyCode);
    },
    taxesTotal() {
      const taxes = this.totalPrice - (this.dailyRate * this.rentalDays);
      return taxes;
    },
    formattedTaxes() {
      const taxes = this.taxesTotal;
      return this.$options.filters.formatPriceFromAmount(Math.max(0, taxes).toFixed(0), 0, this.currencyCode);
    },
    formattedTotal() {
      return this.$options.filters.formatPriceFromAmount(this.totalPrice.toFixed(0), 0, this.currencyCode);
    },
    isBookable() {
      return this.item.isSelectable && this.item.immediateTravelBookingAdvice !== "prevent";
    },
  },
  methods: {
    handleImageError(e) {
      e.target.src = "/images/default-car.png";
    },
    formatHours(hours) {
      if (!hours || !Array.isArray(hours) || hours.length === 0) return null;
      // Find if there's a common pattern (e.g., all days same hours)
      const firstDay = hours[0];
      if (firstDay?.openTime && firstDay?.closeTime) {
        return `Daily ${firstDay.openTime} – ${firstDay.closeTime}`;
      }
      return null;
    },
    viewFullPricingBreakdown() {
      // Open pricing breakdown modal as a layered modal (don't close this one)
      const details = {
        parent: this,
        component: CarPricingBreakdownModal,
        props: {
          item: this.item,
          searchBody: this.searchBody,
          pickupLocation: this.pickupLocation,
        },
        events: {
          book: (selectedItem) => {
            // Propagate booking through both modals
            this.$emit("book", selectedItem);
            this.$emit("close");
          },
        },
        hasModalCard: false,
        trapFocus: true,
        canCancel: ["escape"], // Only allow escape key, not outside click
        customClass: "car-pricing-breakdown-modal-wrapper",
        width: 600,
      };
      this.$buefy.modal.open(details);
    },
    bookCar() {
      this.$emit("book", this.item);
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.car-view-details-modal {
  background: var(--color-background-primary, #fff);
  border-radius: 12px;
  border: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 90vh;
}

.modal-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  background: var(--color-background-primary, #fff);
  flex-shrink: 0;
}

.modal-body-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.vendor-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.vendor-logo {
  height: 28px;
  max-width: 80px;
  object-fit: contain;
  border-radius: 4px;
}

.vendor-name-text {
  font-weight: 600;
  font-size: 14px;
  color: var(--color-text-primary, #333);
}

.header-title {
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text-primary, #333);
}

/* Vehicle Section */
.vehicle-section {
  padding: 1rem 1.25rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  border-bottom: 0.5px solid var(--color-border-tertiary, #e0e0e0);
}

.vehicle-image {
  width: 130px;
  height: 80px;
  object-fit: contain;
  border-radius: 8px;
  background: var(--color-background-secondary, #f5f5f5);
}

.vehicle-info {
  flex: 1;
}

.vehicle-name {
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 4px;
  color: var(--color-text-primary, #333);
}

.vehicle-description {
  font-size: 13px;
  color: var(--color-text-secondary, #666);
  margin: 0 0 8px;
}

.vehicle-specs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.spec-tag {
  font-size: 12px;
  background: var(--color-background-secondary, #f5f5f5);
  border: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  border-radius: 20px;
  padding: 3px 10px;
  color: var(--color-text-secondary, #666);
}

/* Itinerary Section */
.itinerary-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 0.5px solid var(--color-border-tertiary, #e0e0e0);
}

.itinerary-column {
  padding: 1rem 1.25rem;
}

.itinerary-column:first-child {
  border-right: 0.5px solid var(--color-border-tertiary, #e0e0e0);
}

.itinerary-label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--color-text-tertiary, #999);
  margin: 0 0 8px;
}

.itinerary-datetime {
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 2px;
  color: var(--color-text-primary, #333);
}

.itinerary-location {
  font-size: 13px;
  color: var(--color-text-secondary, #666);
  margin: 0 0 10px;
}

.map-placeholder {
  border-radius: 8px;
  overflow: hidden;
  height: 90px;
  background: var(--color-background-secondary, #f5f5f5);
  margin-bottom: 8px;
}

.itinerary-address {
  font-size: 12px;
  color: var(--color-text-secondary, #666);
  margin: 0 0 2px;
}

.itinerary-hours {
  display: flex;
  align-items: center;
  gap: 4px;
}

.hours-label {
  font-size: 11px;
  color: var(--color-text-tertiary, #999);
}

.hours-value {
  font-size: 11px;
  color: var(--color-text-secondary, #666);
}

/* Pricing Section */
.pricing-section {
  padding: 1rem 1.25rem;
  border-bottom: 0.5px solid var(--color-border-tertiary, #e0e0e0);
}

.pricing-label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--color-text-tertiary, #999);
  margin: 0 0 12px;
}

.pricing-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  margin-bottom: 8px;
}

.pricing-description {
  color: var(--color-text-secondary, #666);
}

.pricing-amount {
  color: var(--color-text-primary, #333);
}

.pricing-divider {
  height: 0.5px;
  background: var(--color-border-tertiary, #e0e0e0);
  margin: 10px 0;
}

.pricing-total {
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 0;
}

.pricing-total span {
  color: var(--color-text-primary, #333);
}

.pricing-breakdown-link {
  margin-top: 10px;
  font-size: 13px;
  color: #185FA5;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.pricing-breakdown-link:hover {
  color: #134a82;
}

/* Footer */
.modal-card-foot {
  padding: 1rem 1.25rem;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  background: var(--color-background-primary, #fff);
  border-top: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  flex-shrink: 0;
}

.btn-cancel {
  font-size: 14px;
  padding: 8px 18px;
  border-radius: 8px;
  cursor: pointer;
  background: none;
  border: 0.5px solid var(--color-border-secondary, #ccc);
  color: var(--color-text-secondary, #666);
  transition: all 0.2s;
}

.btn-cancel:hover {
  background: var(--color-background-secondary, #f5f5f5);
}

.btn-book {
  font-size: 14px;
  padding: 8px 22px;
  border-radius: 8px;
  cursor: pointer;
  background: #185FA5;
  border: none;
  color: #fff;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-book:hover {
  background: #134a82;
}

.not-bookable-text {
  font-size: 13px;
  color: #856404;
  font-weight: 500;
  padding: 8px 0;
}

/* Responsive - Mobile */
@media screen and (max-width: 767px) {
  .car-view-details-modal {
    max-width: 100%;
    border-radius: 16px 16px 0 0;
    max-height: calc(100vh - 40px);
    height: calc(100vh - 40px);
  }

  .modal-card {
    height: 100%;
    max-height: calc(100vh - 40px);
  }

  .modal-card-head {
    padding: 0.875rem 1rem;
  }

  .header-title {
    font-size: 14px;
  }

  .modal-body-scroll {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    max-height: none;
    min-height: 0;
  }

  .vehicle-section {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 0.875rem 1rem;
  }

  .vehicle-image {
    width: 100px;
    height: 60px;
    max-width: 100%;
    object-fit: contain;
  }

  .vehicle-name {
    font-size: 15px;
  }

  .vehicle-specs {
    justify-content: center;
    flex-wrap: wrap;
  }

  .itinerary-section {
    grid-template-columns: 1fr;
  }

  .itinerary-column {
    padding: 0.875rem 1rem;
  }

  .itinerary-column:first-child {
    border-right: none;
    border-bottom: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  }

  .map-placeholder {
    height: 70px;
    max-width: 100%;
    overflow: hidden;
  }

  .pricing-section {
    padding: 0.875rem 1rem;
  }

  .modal-card-foot {
    padding: 0.875rem 1rem;
    padding-bottom: calc(0.875rem + env(safe-area-inset-bottom, 0px));
    flex-direction: column;
    gap: 8px;
    flex-shrink: 0;
    background: var(--color-background-primary, #fff);
    border-top: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  }

  .btn-cancel,
  .btn-book {
    width: 100%;
    padding: 14px;
    text-align: center;
    font-size: 15px;
  }

  .btn-cancel {
    order: 2;
  }

  .btn-book {
    order: 1;
  }
}

/* Extra small screens (375px - 414px) */
@media screen and (max-width: 414px) {
  .car-view-details-modal {
    max-height: calc(100vh - 20px);
    height: calc(100vh - 20px);
  }

  .modal-card {
    max-height: calc(100vh - 20px);
  }

  .modal-card-head {
    padding: 0.75rem 0.875rem;
  }

  .vendor-logo {
    height: 24px;
    max-width: 60px;
  }

  .vehicle-section {
    padding: 0.75rem 0.875rem;
  }

  .vehicle-image {
    width: 90px;
    height: 55px;
  }

  .vehicle-name {
    font-size: 14px;
  }

  .vehicle-description {
    font-size: 12px;
  }

  .spec-tag {
    font-size: 11px;
    padding: 2px 6px;
  }

  .itinerary-column {
    padding: 0.75rem 0.875rem;
  }

  .itinerary-label {
    font-size: 11px;
  }

  .itinerary-datetime {
    font-size: 13px;
  }

  .itinerary-location {
    font-size: 12px;
  }

  .map-placeholder {
    height: 60px;
  }

  .pricing-section {
    padding: 0.75rem 0.875rem;
  }

  .pricing-label {
    font-size: 12px;
  }

  .pricing-row {
    font-size: 12px;
  }

  .pricing-breakdown-link {
    font-size: 12px;
  }

  .modal-card-foot {
    padding: 0.75rem 0.875rem;
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  }

  .btn-cancel,
  .btn-book {
    padding: 12px;
    font-size: 14px;
  }
}
</style>

